import { useEffect, useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { PiGoogleLogo } from 'react-icons/pi';
import AuthLayout from '../components/AuthLayout';
import { PasswordField, TextField } from '../components/FormFields';
import { auth } from '../firebaseConfig';
import { getInviteTokenFromLocation, rememberPendingInvite, signInWithGoogle } from '../services/authService';
import { OFFLINE_REQUIRED, requireConnectionForMutation } from '../services/connectivityService';
import './login.css';

const getRegisterMessage = (error) => {
  if (error.code === OFFLINE_REQUIRED) return 'Esta operação exige conexão. Conecte-se e tente novamente.';
  if (error.code === 'auth/email-already-in-use') return 'O endereço de e-mail já está em uso.';
  if (error.code === 'auth/weak-password') return 'A senha deve ter pelo menos 6 caracteres.';
  if (error.code === 'auth/invalid-email') return 'Endereço de e-mail inválido.';
  if (error.code === 'auth/popup-closed-by-user') return 'Cadastro com Google cancelado.';
  if (error.code === 'auth/account-exists-with-different-credential') return 'Já existe uma conta com este e-mail. Entre usando o método original.';
  return 'Não foi possível criar sua conta. Tente novamente.';
};

function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const token = getInviteTokenFromLocation();
    setInviteToken(token);
    rememberPendingInvite(token);
  }, []);

  const handleRegister = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      requireConnectionForMutation();
      await createUserWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (registerError) {
      setError(getRegisterMessage(registerError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleRegister = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      const result = await signInWithGoogle({ inviteToken });
      if (!result.redirected) navigate('/dashboard');
    } catch (googleError) {
      setError(getRegisterMessage(googleError));
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Criar conta" subtitle="Comece a organizar suas finanças de forma simples.">
      <button className="auth-google-button" type="button" onClick={handleGoogleRegister} disabled={isSubmitting}>
        <PiGoogleLogo size={20} aria-hidden="true" />
        Continuar com Google
      </button>
      <div className="auth-divider"><span>ou use seu e-mail</span></div>
      <form className="auth-form" onSubmit={handleRegister}>
        {error && <div className="auth-alert auth-alert--error" role="alert">{error}</div>}
        <TextField id="register-email" label="E-mail" type="email" inputMode="email" autoComplete="email" value={email} onChange={setEmail} required disabled={isSubmitting} />
        <PasswordField id="register-password" label="Senha" hint="Use pelo menos 6 caracteres." autoComplete="new-password" value={password} onChange={setPassword} required disabled={isSubmitting} />
        <button className="auth-primary-button" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Criando conta…' : 'Criar conta'}</button>
      </form>
      <p className="auth-switch">Já tem uma conta? <Link to={`/login${inviteToken ? `?invite=${inviteToken}` : ''}`}>Entrar</Link></p>
    </AuthLayout>
  );
}

export default Register;
